"use client"
import { FaDownload, FaCheckCircle, FaDollarSign, FaStar } from "react-icons/fa"
import { MdEdit, MdVisibility } from "react-icons/md"
import { formatShortDate, CustomTooltip } from '../../utils/helpers';
import "./QueryTableCard.scss"

const QueryTableCard = ({ rowData, onDownloadClick, onEditClick }) => {
    const isClosed = rowData.resolutionContent?.includes("Closed")
    const nettCharge =
        rowData.queryCorrectNett?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || "0.00"
    const rateBilled =
        rowData.rateBilled?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || "0.00"
    const resolvedNett =
        rowData.resolvedNett?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || "0.00"
    const billedNett =
        rowData.billednett?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || "0.00"

    // Determine status modifier class based on category or closed state
    const getStatusClass = () => {
        if (isClosed) return "query-card-v2--closed"
        const category = rowData.querycategory?.toLowerCase()
        if (category === "billing") return "query-card-v2--billing"
        if (category === "quality") return "query-card-v2--quality"
        return ""
    }

    const getCategoryIcon = () => {
        const category = rowData.querycategory?.toLowerCase()
        if (category === 'billing') {
            return <FaDollarSign color="#77c7af" size={18} />
        } else if (category === 'quality') {
            return <FaStar color="#fcc222" size={18} />
        }
        return null
    }

    const getEditIcon = () => {
        if (rowData.editable && !isClosed) {
            return <MdEdit color="#7b93ff" size={18} />
        } else {
            return <MdVisibility color="rgba(255,255,255,0.5)" size={18} />
        }
    }

    return (
        <div className={`query-card-v2 mb-3 ${getStatusClass()}`}>
            {/* Header — site reference and status icons */}
            <div className="query-card-v2__header">
                <h3 className="query-card-v2__title">Site: {rowData.siteref || "N/A"}</h3>
                <div className="query-card-v2__icons">
                    <CustomTooltip content={rowData.querycategory || "Category"}>
                        {getCategoryIcon()}
                    </CustomTooltip>
                    <CustomTooltip content={rowData.editable && !isClosed ? "Editable" : "Read Only"}>
                        {getEditIcon()}
                    </CustomTooltip>
                </div>
            </div>

            {/* Two-column data grid */}
            <div className="query-card-v2__grid">
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Query ID</span>
                    <span className="query-card-v2__value">{rowData.id || "N/A"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Line ID</span>
                    <span className="query-card-v2__value">{rowData.lineID || "N/A"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Received</span>
                    <span className="query-card-v2__value">{formatShortDate(rowData.dateLogged)}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Query Type</span>
                    <span className="query-card-v2__value">{rowData.querytype || "N/A"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Raised by</span>
                    <span className="query-card-v2__value">{rowData.raisedby || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Site Name</span>
                    <span className="query-card-v2__value">{rowData.sitename || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Role Department</span>
                    <span className="query-card-v2__value">{rowData.roledepartment || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Job Date</span>
                    <span className="query-card-v2__value">{formatShortDate(rowData.dateOfJob)}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Date Resolved</span>
                    <span className="query-card-v2__value">{formatShortDate(rowData.dateResolved)}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Credited</span>
                    <span className="query-card-v2__value">{rowData.credited || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Provider</span>
                    <span className="query-card-v2__value">{rowData.providerName || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Staff Name</span>
                    <span className="query-card-v2__value">{rowData.rolename || "-"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Hours Billed</span>
                    <span className="query-card-v2__value">{rowData.hrsBilled || "0"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Correct Hours</span>
                    <span className="query-card-v2__value">{rowData.queryCorrectHrs || "0"}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Rate Billed</span>
                    <span className="query-card-v2__value">&pound;{rateBilled}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Billed Nett</span>
                    <span className="query-card-v2__value">&pound;{billedNett}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Correct Nett</span>
                    <span className="query-card-v2__value">&pound;{nettCharge}</span>
                </div>
                <div className="query-card-v2__field">
                    <span className="query-card-v2__label">Resolved Nett</span>
                    <span className="query-card-v2__value">&pound;{resolvedNett}</span>
                </div>
            </div>

            {/* Query content (if available) */}
            {rowData.queryContent && (
                <div className="query-card-v2__content">
                    <span className="query-card-v2__label">Query Content</span>
                    <span className="query-card-v2__value">{rowData.queryContent}</span>
                </div>
            )}

            {/* Action buttons */}
            <div className="query-card-v2__actions">
                <button
                    onClick={() => onEditClick(rowData)}
                    className="query-card-v2__btn query-card-v2__btn--primary"
                >
                    {rowData.editable && !isClosed ? 'Edit Query' : 'View Query'}
                </button>
                {(rowData.uRefForAzureImages || rowData.providerEvidence) && (
                    <button
                        onClick={() => onDownloadClick(rowData)}
                        className="query-card-v2__btn query-card-v2__btn--glass"
                    >
                        <FaDownload size={13} />
                        Download
                    </button>
                )}
                {isClosed && (
                    <div className="query-card-v2__btn query-card-v2__btn--resolved">
                        <FaCheckCircle size={13} />
                        Resolved
                    </div>
                )}
            </div>
        </div>
    )
}

export default QueryTableCard
